import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew, { PopupNewHandle } from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import SaveMoneyPopup from 'apps/auth/components/ga/accounts/send/popups/SaveMoneyPopup';
import { PROMO_ALREADY_APPLIED_BOTTOM_SHEET_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PROMO_CODE_FIELD } from 'apps/auth/constants/fieldsName';
import type { RootState } from 'apps/auth/redux/store';
import { MarkdownService } from 'apps/auth/services/MarkdownService';

import styles from './SaveMoneyButton.module.css';

interface OwnProps {
    formName: string;
    promoCodeFetchUrl: string;
    beforeSaveMoneyCodeSubmit?: () => void;
    commissionInfo?: {
        bonusSpecialRate?: boolean;
        specialBonusRateInfo?: boolean;
        bonusSpecialCommission?: boolean;
    };
}

interface StateProps {
    saveMoney: string;
    promotionApplied: string;
    gotIt: string;
    feeRatePopupButtonClose: string;
    promoCode: { value: string };
    canUsePromoCode: boolean;
}

type Props = OwnProps & StateProps;

type AmplitudeInstance = {
    logEvent: (event: string, payload?: Record<string, unknown>) => void;
};
const amplitudeInstance = amplitude as { getInstance: () => AmplitudeInstance };

const SaveMoneyButton = (props: Props) => {
    const popupRef = useRef<PopupNewHandle>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const showSaveMoneyLink = props.canUsePromoCode && !props.promoCode.value;
    const showPromotionAppliedPopUp = showSaveMoneyLink && props.commissionInfo?.bonusSpecialCommission;

    if (!showSaveMoneyLink && !isPopupOpen) {
        return null;
    }

    return (
        <>
            {showSaveMoneyLink && (
                <button
                    className={styles.saveButton}
                    onClick={(e) => {
                        e.preventDefault();
                        if (showPromotionAppliedPopUp) {
                            amplitudeInstance.getInstance().logEvent(PROMO_ALREADY_APPLIED_BOTTOM_SHEET_SHOWN);
                        }
                        setIsPopupOpen(true);
                        popupRef.current?.open(
                            showPromotionAppliedPopUp ? (
                                <SimplePopup text={MarkdownService.splitByParagraph(props.promotionApplied, [])} popupRef={popupRef}>
                                    <PopupButton
                                        title={props.gotIt}
                                        iconName="check"
                                        text={props.feeRatePopupButtonClose}
                                        onClick={() => {
                                            popupRef.current?.close();
                                            setIsPopupOpen(false);
                                        }}
                                    />
                                </SimplePopup>
                            ) : (
                                <SaveMoneyPopup
                                    formName={props.formName}
                                    promoCodeFetchUrl={props.promoCodeFetchUrl}
                                    beforeSaveMoneyCodeSubmit={props.beforeSaveMoneyCodeSubmit}
                                    popupRef={popupRef}
                                />
                            ),
                        );
                    }}
                >
                    <Text size="secondary" variant="span" color={showPromotionAppliedPopUp ? 'lightGray' : 'primary'}>
                        {props.saveMoney}
                    </Text>
                </button>
            )}
            <PopupNew ref={popupRef} />
        </>
    );
};
const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
    const messages = state.messages as { pageMessages: Record<string, string> };
    const extra = state.extra as { canUsePromoCode: boolean };
    const form = state.form as Record<string, { fields: Record<string, { value: string }> }>;

    return {
        saveMoney: messages.pageMessages.saveMoney,
        promotionApplied: messages.pageMessages.promotionApplied,
        gotIt: messages.pageMessages.gotIt,
        feeRatePopupButtonClose: messages.pageMessages.feeRatePopupButtonClose,
        promoCode: form[ownProps.formName].fields[PROMO_CODE_FIELD],
        canUsePromoCode: extra.canUsePromoCode,
    };
};

export default connect(mapStateToProps)(SaveMoneyButton);
